// Date helpers. All "date keys" are local ISO dates: YYYY-MM-DD.
// All "time keys" are 24h strings: HH:mm.

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n) => String(n).padStart(2, '0');

export const toDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const fromDateKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const todayKey = () => toDateKey(new Date());

/** Local timestamp without timezone: YYYY-MM-DDTHH:mm:ss */
export const nowLocalIso = () => {
  const d = new Date();
  return `${toDateKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

export const addMonths = (date, n) => new Date(date.getFullYear(), date.getMonth() + n, 1);

export const isSameDay = (a, b) => toDateKey(a) === toDateKey(b);

/** Monday of the week containing `date`. */
export const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() + 6) % 7;
  return addDays(d, -diff);
};

export const weekDays = (date) => {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

/** 6x7 (or 5x7) Monday-first grid for a month; null for padding cells. */
export const monthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(lead).fill(null);
  for (let i = 1; i <= days; i += 1) cells.push(new Date(year, month, i));
  while (cells.length % 7) cells.push(null);
  return cells;
};

export const dayShort = (d) => DAY_SHORT[d.getDay()];
export const dayLong = (d) => DAY_LONG[d.getDay()];
export const monthShort = (d) => MONTH_SHORT[d.getMonth()];
export const monthLong = (d) => MONTH_LONG[d.getMonth()];

const asDate = (v) => (typeof v === 'string' ? fromDateKey(v) : v);

/** Tue, 6 Oct */
export const formatDayDate = (v) => {
  const d = asDate(v);
  return `${dayShort(d)}, ${d.getDate()} ${monthShort(d)}`;
};

/** Tue 6 Oct */
export const formatDayDateNoComma = (v) => {
  const d = asDate(v);
  return `${dayShort(d)} ${d.getDate()} ${monthShort(d)}`;
};

/** Monday, 19 October */
export const formatLongDate = (v) => {
  const d = asDate(v);
  return `${dayLong(d)}, ${d.getDate()} ${monthLong(d)}`;
};

/** 14 Sep (adds year if not current year) */
export const formatShortDate = (v) => {
  const d = asDate(v);
  const base = `${d.getDate()} ${monthShort(d)}`;
  return d.getFullYear() === new Date().getFullYear() ? base : `${base} ${d.getFullYear()}`;
};

/** 6 October 2026 */
export const formatFullDate = (v) => {
  const d = asDate(v);
  return `${d.getDate()} ${monthLong(d)} ${d.getFullYear()}`;
};

/** "09:30" -> "9:30 AM" */
export const formatTime = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${pad(m)} ${suffix}`;
};

export const timeToMinutes = (time) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const minutesToTime = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

export const toDateTime = (dateKey, time) => {
  const d = fromDateKey(dateKey);
  const [h, m] = time.split(':').map(Number);
  d.setHours(h, m, 0, 0);
  return d;
};

export const daysBetween = (a, b) => Math.round((asDate(b) - asDate(a)) / 86400000);

export const greeting = (d = new Date()) => {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/** "19–25 Oct" or "28 Sep – 4 Oct" */
export const formatRange = (start, end) => {
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()}–${end.getDate()} ${monthShort(end)}`;
  }
  return `${start.getDate()} ${monthShort(start)} – ${end.getDate()} ${monthShort(end)}`;
};
